/**
 * HomeLandingGate — mode-aware landing for the root "/" route.
 *
 * The "/" route renders the Sky Advisor dashboard. For a tenant on the
 * senior_living (operator_crm) pack whose active view mode is CRM, the
 * Advisor dashboard is the wrong landing on a cold open / direct nav to
 * "/" — the user should arrive in Sky CRM, which then role-routes
 * (manager/admin -> Overview, counselor / no-link -> Day Planner) via
 * CrmLandingRedirect. We deliberately do NOT re-implement that
 * destination logic here: this gate only decides "Advisor home vs CRM",
 * and delegates "where in CRM" to the single source of truth at /crm.
 *
 * Why a guard and not a redirect inside the dashboard component: the "/"
 * route resolves to one of two lazy components (Home / SkyAdvisorDashboard,
 * selected by hideNewdashboard). Gating at the route — exactly like
 * PackRouteGuard does for /crm/* in AppLayout — covers whichever one is
 * active and keeps the dashboard component presentational.
 *
 * Load-race safety: PackProvider's installed-packs query is in flight on a
 * fresh page load, during which packs === [] and currentMode transiently
 * resolves to 'lead_gen_only'. We defer the decision while isLoading
 * (mirrors PackRouteGuard) so the gate can't fall back to Advisor mid-load
 * and strand a CRM user on the dashboard. location.search is preserved so
 * ?instanceid=... survives the redirect (CrmLandingRedirect preserves it
 * onward).
 */
import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { usePacks } from "./packs/PackContext";

export function HomeLandingGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isLoading, currentMode, hasOperatorPack } = usePacks();

  // Defer until installed packs resolve — never decide mid-load.
  if (isLoading) {
    return (
      <div
        className="p-6 flex justify-center"
        role="status"
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading home"
      >
        <div
          className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  // CRM-mode tenants land in Sky CRM (-> role-based /crm landing).
  // hasOperatorPack (not a hardcoded pack id) — any operator-mode pack
  // owns the CRM landing (Phase-0 de-hardcoding).
  if (currentMode === "operator_crm" && hasOperatorPack) {
    return (
      <Navigate
        to={"/crm" + location.search}
        replace
      />
    );
  }

  // Advisor mode (or non-CRM tenant) -> the Advisor dashboard, unchanged.
  return <>{children}</>;
}

export default HomeLandingGate;
